import api from "@/lib/api";

/** Batched analytics emitter. Coalesces impressions across the page and flushes every 1.5s. */
let queue = [];
let timer = null;

const flush = () => {
  if (queue.length === 0) return;
  const events = {};
  for (const e of queue) {
    events[e.event] = events[e.event] || new Set();
    events[e.event].add(e.product_id);
  }
  queue = [];
  for (const [event, ids] of Object.entries(events)) {
    api
      .post("/analytics/track", { product_ids: Array.from(ids), event })
      .catch(() => {});
  }
};

export const trackEvent = (event, product_id) => {
  if (!product_id) return;
  queue.push({ event, product_id });
  if (!timer) {
    timer = setTimeout(() => {
      flush();
      timer = null;
    }, 1500);
  }
};

// Flush on page hide
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flush);
  window.addEventListener("beforeunload", flush);
}

// ---------------------------------------------------------------------------
// Operator funnel tracking — fires straight at /api/analytics/visit. These
// events power the Admin Console analytics tab and are intentionally kept
// independent from trackEvent above, which is locked to product_ids and
// feeds the per-shop dashboard the dispensary owners look at.
// ---------------------------------------------------------------------------

// In-tab dedupe so a quick double-render of the same path doesn't double-
// count visits (React StrictMode in dev double-mounts everything).
const _seen = { path: "", search: "", disp: "", zip: "" };

const _post = (body) => {
  try {
    api.post("/analytics/visit", body).catch(() => {});
  } catch (_) {
    /* network errors are deliberately swallowed — analytics must never break the page */
  }
};

export const trackPageView = (path) => {
  if (!path) return;
  // Strip the query string before dedupe so /search?q=blue+dream and
  // /search?q=og+kush count as ONE page-view (the search itself is
  // captured separately via trackSearch).
  const clean = path.split("?")[0];
  if (clean === _seen.path) return;
  _seen.path = clean;
  _post({
    event: "page_view",
    path: clean,
    referrer: typeof document !== "undefined" ? document.referrer : "",
  });
};

export const trackSearch = (q) => {
  const v = (q || "").trim();
  if (!v || v === _seen.search) return;
  _seen.search = v;
  _post({ event: "search", q: v });
};

export const trackDispensaryView = (slug) => {
  if (!slug || slug === _seen.disp) return;
  _seen.disp = slug;
  _post({ event: "dispensary_view", dispensary_slug: slug });
};

export const trackZipSet = (zip) => {
  const v = (zip || "").trim();
  if (!v || v === _seen.zip) return;
  _seen.zip = v;
  _post({ event: "zip_set", zip: v });
};


